/**
 * The library's browsing state, made durable — R-6, R-9, R-D1.
 *
 * Filters used to be six `useState` calls in `Library.tsx`, which died the
 * moment the component unmounted. That was fine while the grid was the only
 * screen that cared. It stopped being fine when opening a tab and coming back
 * had to land the reader on the card they left, with the filter still on.
 *
 * The split is deliberate:
 *
 * - **Filters go in the URL.** They describe *what you are looking at*, so
 *   they belong in the thing you can send someone. A filtered library has never
 *   been a shareable link and now is.
 * - **Scroll goes in `sessionStorage`.** It describes *where this browser is*,
 *   and a shared link that says "start 1400px down a list you have not seen"
 *   is nonsense.
 * - **View mode stays in `localStorage`** where §24 already put it. A standing
 *   preference, not a description of one query.
 *
 * Encoding lives here rather than in the screen so there is exactly one place
 * that knows the param names. A filter serialised differently by the reader and
 * the writer fails *silently* — the grid just shows everything — which is the
 * kind of bug that survives a demo.
 */

export type SortKey = "title" | "game" | "difficulty" | "added";
export type Direction = "asc" | "desc";
export type ViewMode = "grid" | "list";

/**
 * Which way the difficulty filter compares — DF-D4.
 *
 * The filter used to be a floor and nothing else, which was the wrong default
 * for who uses it: a patron asking about difficulty is usually asking "what can
 * I actually play?", and the answer to that is a ceiling. `lte` is the default
 * for that reason.
 */
export type DiffMode = "lte" | "eq" | "gte";

const DIFF_MODES: readonly DiffMode[] = ["lte", "eq", "gte"];

export type GridFilters = {
  query: string;
  /** Which way {@link GridFilters.diffValue} compares. */
  diffMode: DiffMode;
  /** The difficulty being compared against, 0 meaning "no difficulty filter". */
  diffValue: number;
  franchise: string | null;
  /** A game **id**, not a slug — see decision R-D4. */
  game: string | null;
  sortKey: SortKey;
  direction: Direction;
};

export const DEFAULT_FILTERS: GridFilters = {
  query: "",
  diffMode: "lte",
  diffValue: 0,
  franchise: null,
  game: null,
  // §7: the library reads newest-first until told otherwise.
  sortKey: "added",
  direction: "desc",
};

const PARAM = {
  query: "q",
  /** Carries mode and value together — see {@link readDiff}. */
  diff: "diff",
  franchise: "from",
  game: "game",
  sortKey: "sort",
  direction: "dir",
} as const;

const SORT_KEYS: readonly SortKey[] = ["title", "game", "difficulty", "added"];
const DIRECTIONS: readonly Direction[] = ["asc", "desc"];

/**
 * The difficulty filter, read out of one parameter — FR-7, DF-D2.
 *
 * Grammar: `(lte|eq|gte)?[1-5]?`, at least one part present.
 *
 *     ?diff=lte3   at most 3
 *     ?diff=eq3    exactly 3
 *     ?diff=gte4   at least 4
 *     ?diff=gte    a direction with no level chosen yet
 *     ?diff=3      REFUSED — see below
 *
 * **A bare number is refused on purpose.** It is the grammar this filter used
 * before 2026-09-12, when it meant "3 and up", and it now means the opposite.
 * Accepting it would silently reinterpret every link anyone has shared — the
 * same class of defect this feature exists to fix, just at a distance. A stale
 * link that shows the whole library is a visible, recoverable failure; one that
 * quietly filters the other way is not.
 *
 * **`?diff=gte` with no digit is legal** because `Library` derives its filters
 * from the URL on every render and holds none of its own. A mode that is not
 * serialised does not survive the next navigation, so "clear the level, keep
 * the direction" would fail the moment anything re-read the query string.
 */
function readDiff(raw: string | null): Pick<GridFilters, "diffMode" | "diffValue"> {
  const fallback = {
    diffMode: DEFAULT_FILTERS.diffMode,
    diffValue: DEFAULT_FILTERS.diffValue,
  };
  if (!raw) return fallback;

  const match = /^([a-z]*)([1-5]?)$/.exec(raw);
  if (!match) return fallback;
  const [, mode, value] = match;

  // The mode is required even when it is the default, so that a bare `?diff=3`
  // is refused rather than silently reinterpreted. The writer always emits it
  // alongside a value for the same reason.
  if (!mode || !DIFF_MODES.includes(mode as DiffMode)) return fallback;

  return { diffMode: mode as DiffMode, diffValue: value ? Number(value) : 0 };
}

/**
 * Read filters out of a query string.
 *
 * Every field falls back to its default rather than throwing or propagating a
 * bad value. A URL is user-editable and arrives from links people forward, so
 * junk in it is an ordinary input, not an exceptional one. `?diff=banana` must
 * mean "no difficulty filter" — `Number("banana")` is `NaN`, and `NaN` compares
 * false against everything, so an unguarded parse empties the library and reads
 * as "you have no tabs".
 */
export function readFilters(params: URLSearchParams): GridFilters {
  const { diffMode, diffValue } = readDiff(params.get(PARAM.diff));

  const sort = params.get(PARAM.sortKey) as SortKey | null;
  const dir = params.get(PARAM.direction) as Direction | null;

  return {
    query: params.get(PARAM.query) ?? "",
    diffMode,
    diffValue,
    // An absent param and one set to empty are the same thing for a filter:
    // both mean "not filtering by this". `?from=` should not match a franchise
    // whose name is the empty string.
    franchise: params.get(PARAM.franchise) || null,
    game: params.get(PARAM.game) || null,
    sortKey: sort && SORT_KEYS.includes(sort) ? sort : DEFAULT_FILTERS.sortKey,
    direction: dir && DIRECTIONS.includes(dir) ? dir : DEFAULT_FILTERS.direction,
  };
}

/**
 * Write filters into a query string, **omitting defaults**.
 *
 * So an unfiltered library is `/tabs`, not
 * `/tabs?q=&diff=0&sort=added&dir=desc`. That matters for more than tidiness:
 * the back button's target is compared against `/tabs` in places, and a URL
 * carrying every default is a different string for the same state.
 */
/**
 * Filters -> a query string, **keeping any param this module does not own.**
 *
 * `existing` is the URL being edited. Building from scratch instead was a real
 * defect, reported 2026-09-01: while previewing a tier, every keystroke in the
 * search box fired a request and a loading indicator, where a patron's typing
 * was instant and client-side.
 *
 * The mechanism, since it is not obvious from here: dropping `?as=` flips
 * `PreviewProvider`'s remount key, which remounts the library and refetches;
 * its restore effect then puts `as` back, flipping the key again and refetching
 * a second time. Two remounts per character typed.
 *
 * `as` was only the visible instance — *any* unrelated param was destroyed by
 * typing. So this preserves everything and owns only `PARAM`.
 *
 * The delete pass matters as much as the copy: without it, carrying the
 * previous params forward would strand a filter that has just been cleared,
 * making it impossible to turn off.
 */
export function writeFilters(
  filters: GridFilters,
  existing?: URLSearchParams
): URLSearchParams {
  const params = new URLSearchParams(existing);
  for (const key of Object.values(PARAM)) params.delete(key);
  if (filters.query) params.set(PARAM.query, filters.query);
  /*
   * Mode and value are omitted independently, so the default mode with no value
   * writes nothing at all and `/tabs` stays `/tabs`. A non-default mode is
   * written even with no value (`?diff=gte`) — DF-D2 — because dropping it
   * would lose the reader's chosen direction on the next navigation.
   */
  if (filters.diffValue > 0) {
    // The mode goes in even when it is the default: `?diff=3` is the old
    // grammar and the reader refuses it, so omitting `lte` here would write a
    // URL this module cannot read back.
    params.set(PARAM.diff, `${filters.diffMode}${filters.diffValue}`);
  } else if (filters.diffMode !== DEFAULT_FILTERS.diffMode) {
    params.set(PARAM.diff, filters.diffMode);
  }
  if (filters.franchise) params.set(PARAM.franchise, filters.franchise);
  if (filters.game) params.set(PARAM.game, filters.game);
  if (filters.sortKey !== DEFAULT_FILTERS.sortKey) params.set(PARAM.sortKey, filters.sortKey);
  if (filters.direction !== DEFAULT_FILTERS.direction) {
    params.set(PARAM.direction, filters.direction);
  }
  return params;
}

/**
 * The search half of the URL, ready to hand to `navigate`.
 *
 * Takes `existing` for the same reason `writeFilters` does: the breadcrumb's
 * game link navigates through here, so without it, clicking the game while
 * previewing dropped `?as=` — the same defect on a different trigger.
 */
export const filtersToSearch = (
  filters: GridFilters,
  existing?: URLSearchParams
): string => {
  const q = writeFilters(filters, existing).toString();
  return q ? `?${q}` : "";
};

/**
 * Does one arrangement's difficulty pass the filter? — FR-2.
 *
 * Here rather than in the screen for the reason the rest of this module is
 * here: there should be exactly one place that knows what a filter means. It
 * lived in `Library.tsx` as a single line for three weeks, filtering the wrong
 * way, with **no test anywhere in the repo** — 17 tests in `Library.test.tsx`
 * and not one touched difficulty. That is how the direction survived.
 *
 * `difficulty` is whatever the asset carries, which may be absent: the field is
 * declared without `required`, so a creator can save a tab with no rating. It
 * coerces to 0, which passes `lte` at every level and fails `eq` and `gte` at
 * every level. That is a **consequence, not a choice** — see open question 3 in
 * the spec. It is pinned by a test so that changing it has to be deliberate.
 */
export function matchesDifficulty(
  difficulty: number,
  mode: DiffMode,
  value: number
): boolean {
  if (value <= 0) return true;
  switch (mode) {
    case "lte":
      return difficulty <= value;
    case "eq":
      return difficulty === value;
    case "gte":
      return difficulty >= value;
  }
}

/** R-8: the game link is a new query, not a restoration of the old one. */
export const onlyGame = (gameId: string): GridFilters => ({
  ...DEFAULT_FILTERS,
  game: gameId,
});

/* ---- session state ------------------------------------------------------
   Everything below touches `sessionStorage`, which throws in a few real
   situations - Safari private mode historically, and any browser configured to
   block site data. None of it is load-bearing: a failed read means no scroll
   restore and no stepper, which is the deep-link experience. So every access is
   wrapped, and a failure is silence rather than a broken screen. */

const SCROLL_KEY = "perks.grid.scroll";
const COLLECTION_KEY = "perks.grid.collection";

const session = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

export function rememberScroll(offset: number): void {
  try {
    session()?.setItem(SCROLL_KEY, String(Math.max(0, Math.round(offset))));
  } catch {
    /* nothing to do - the reader loses a scroll position, not their place */
  }
}

export function recallScroll(): number {
  try {
    const raw = Number(session()?.getItem(SCROLL_KEY));
    return Number.isFinite(raw) && raw > 0 ? raw : 0;
  } catch {
    return 0;
  }
}

/**
 * The ordered list of slugs the reader was looking at — R-9.
 *
 * Stored, but **never read on its own**: the detail screen reads it only when
 * the navigation that arrived carried the "came from the grid" flag (R-D2).
 * Stored data alone would make a shared link opened an hour later look like an
 * arrival, and the stepper would walk somebody else's filters.
 */
export function rememberCollection(slugs: readonly string[]): void {
  try {
    session()?.setItem(COLLECTION_KEY, JSON.stringify(slugs));
  } catch {
    /* the stepper hides; the breadcrumb does not depend on this */
  }
}

export function recallCollection(): string[] {
  try {
    const raw = session()?.getItem(COLLECTION_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    // Written by us, but read after an unknown amount of time and any number of
    // deploys. A shape check is cheaper than the crash.
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((s): s is string => typeof s === "string");
  } catch {
    return [];
  }
}

/**
 * The flag the grid sets when it opens a tab, and the only thing that makes a
 * stored collection trustworthy — R-D2.
 *
 * Read from React Router's navigation state rather than from storage, because
 * storage answers "was a list ever stored" and this has to answer "did *this
 * navigation* come from the grid". Those differ for the reader who browsed an
 * hour ago and then opened a shared link in the same tab: stored data, no
 * arrival.
 *
 * The consequence is that reloading a tab loses the stepper, since navigation
 * state does not survive a reload. That is the correct answer — after a reload
 * there genuinely is no arrival list — and it is written down here because it
 * will otherwise look like a bug.
 */
export type GridArrival = { fromGrid?: boolean };

export function collectionFor(
  state: unknown,
  slug: string
): { collection: string[]; index: number } | null {
  if (!(state as GridArrival | null)?.fromGrid) return null;

  const collection = recallCollection();
  const index = collection.indexOf(slug);
  // Arrived from the grid, but this slug is not in the list we stored - a
  // filter changed underneath, or the storage write failed. Hiding the stepper
  // is better than showing a position that does not describe anything.
  if (index === -1) return null;

  return { collection, index };
}
